import type { CityConfig } from "../city-template/types";

export const newportBeachConfig: CityConfig = {
  city: "Newport Beach",
  state: "California",
  stateAbbr: "CA",
  slug: "newport-beach",
  route: "/cities/newport-beach",
  title: "Emergency Passport Newport Beach, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Newport Beach, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for coastal Orange County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Newport Beach, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Newport Beach and coastal Orange County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Newport%20Beach%20California%20harbor%20at%20golden%20hour%20with%20luxury%20yachts%20and%20sailboats%20docked%20in%20the%20marina%2C%20Fashion%20Island%20skyline%20visible%20in%20the%20background%2C%20palm%20trees%20along%20the%20waterfront%20promenade%2C%20warm%20amber%20sunset%20light%20reflecting%20on%20calm%20water%2C%20professional%20coastal%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20upscale%20coastal%20environment&width=1800&height=700&seq=newport-hero-001&orientation=landscape",
  heroImageAlt: "Newport Beach California harbor at golden hour with luxury yachts and coastal skyline",
  geoPosition: "33.6189;-117.9289",
  geoPlacename: "Newport Beach, California",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "300 N Los Angeles St, Los Angeles, CA 90012",
    distance: "Located in downtown LA — approximately 45–60 minutes from Newport Beach via I-405",
  },
  nearbyCitiesZips: [
    { name: "Newport Beach", zips: "92660, 92661, 92663" },
    { name: "Corona del Mar", zips: "92625" },
    { name: "Costa Mesa", zips: "92626, 92627" },
    { name: "Irvine", zips: "92602–92604, 92606, 92612, 92614, 92617–92620" },
    { name: "Laguna Beach", zips: "92651, 92652" },
    { name: "Huntington Beach", zips: "92646–92649" },
    { name: "Fountain Valley", zips: "92708, 92728" },
    { name: "Newport Coast", zips: "92657" },
    { name: "Balboa Island", zips: "92662" },
    { name: "Santa Ana", zips: "92701–92707" },
    { name: "Anaheim", zips: "92801–92808" },
    { name: "Tustin", zips: "92780, 92782" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent coastal Orange County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Irvine", route: "/cities/irvine" },
    { name: "Costa Mesa", route: "/cities/costa-mesa" },
    { name: "Huntington Beach", route: "/cities/huntington-beach" },
    { name: "Santa Ana", route: "/cities/santa-ana" },
    { name: "Anaheim", route: "/cities/anaheim" },
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "San Diego", route: "/cities/san-diego" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Newport Beach remotely via secure courier. We do not have a walk-in office in Newport Beach — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for coastal Orange County travelers.",
  fpcOfficeAddress: "200 Spectrum Center Dr, Irvine, CA 92618",
  fpcOfficeDistance: "Serving all of coastal Orange County",
  serviceAreaIntro: "From Balboa Island to Corona del Mar, from Newport Coast to the Back Bay — Fast Passport Center serves the entire Newport Beach community. We regularly help travelers from Costa Mesa, Laguna Beach, Huntington Beach, and Irvine get their passports fast.",
  nearbyCitiesIntro: "We serve all Newport Beach neighborhoods and nearby coastal Orange County cities including Costa Mesa, Laguna Beach, Huntington Beach, Fountain Valley, and Irvine. If you are in coastal Orange County, we have you covered.",
  howItWorksIntro: "Most Newport Beach travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With John Wayne Airport (SNA) just minutes away and LAX a short drive up the coast, Newport Beach travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Newport Beach and coastal Orange County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Newport Beach? Here are the most common ones we hear from coastal Orange County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent coastal Orange County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Richard H.",
      location: "Newport Beach, CA",
      rating: 5,
      text: "My flight to Paris was in 2 days and my passport had expired. Fast Passport Center got me a new one in 30 hours. Incredible service for Newport Beach residents.",
      service: "Emergency Passport",
    },
    {
      name: "Catherine M.",
      location: "Corona del Mar, CA",
      rating: 5,
      text: "Needed to renew for a family cruise to the Mediterranean. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Thomas B.",
      location: "Laguna Beach, CA",
      rating: 5,
      text: "Lost my passport a week before my honeymoon to Fiji. They walked me through the DS-64 process and got me a replacement in 48 hours. Saved our trip.",
      service: "Lost Passport",
    },
    {
      name: "Amanda L.",
      location: "Costa Mesa, CA",
      rating: 5,
      text: "As a frequent international traveler from coastal Orange County, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "nb-1",
      name: "Newport Beach Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1860 San Joaquin Hills Rd",
      city: "Newport Beach",
      state: "CA",
      zip: "92660",
      phone: "(949) 644-3249",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from Fashion Island",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "nb-2",
      name: "Costa Mesa Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1835 Newport Blvd",
      city: "Costa Mesa",
      state: "CA",
      zip: "92627",
      phone: "(949) 548-1024",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2.5 miles from Newport Beach border",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "nb-3",
      name: "Irvine Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "15642 Sand Canyon Ave",
      city: "Irvine",
      state: "CA",
      zip: "92618",
      phone: "(949) 753-0398",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5.8 miles from Newport Beach",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for central Orange County. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "nb-4",
      name: "Huntington Beach Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3300 Main St",
      city: "Huntington Beach",
      state: "CA",
      zip: "92648",
      phone: "(714) 536-4241",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8.2 miles from Newport Beach",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for northern OC coast residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "nb-5",
      name: "Laguna Beach Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "240 Broadway St",
      city: "Laguna Beach",
      state: "CA",
      zip: "92651",
      phone: "(949) 494-3645",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "7.5 miles from Newport Beach",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for south coast OC residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "nb-6",
      name: "Orange County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "333 W Santa Ana Blvd",
      city: "Santa Ana",
      state: "CA",
      zip: "92701",
      phone: "(714) 834-2500",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "10 miles from Newport Beach",
      website: "https://www.ocassessor.com/",
      websiteLabel: "OC Clerk-Recorder",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown LA", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Coastal OC photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Newport Beach area acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many coastal OC applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the LA regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Newport Beach families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Newport Beach travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Newport Beach." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Newport Beach address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Newport Beach before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Newport Beach for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Orange County coastal travelers." },
    { q: "How fast can I renew my passport in Newport Beach?", a: "Fast Passport Center offers fast passport renewal in Newport Beach in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Newport Beach in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Newport Beach?", a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Newport Beach residents." },
    { q: "Can I expedite a child passport in Newport Beach?", a: "Yes. Fast Passport Center expedites child passports in Newport Beach for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Newport Beach with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Newport Beach?",
      a: "With Fast Passport Center's emergency courier service, Newport Beach travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Los Angeles Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Newport Beach?",
      a: "Popular options include the Newport Beach Post Office (92660), Costa Mesa Post Office (92627), Irvine Post Office (92618), and Orange County Clerk (92701). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Newport Beach?",
      a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Corona del Mar or Newport Coast?",
      a: "We serve all Newport Beach neighborhoods including Corona del Mar, Newport Coast, Balboa Island, and the Newport Back Bay. Our overnight shipping labels work from any UPS location, so your location within Newport Beach doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Newport Beach?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Newport Beach?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Newport Beach, Costa Mesa, and Irvine offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Newport Beach?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Newport Beach?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Newport Beach families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Newport Beach", address: "1500 San Miguel Dr, Newport Beach, CA 92660", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Costa Mesa", address: "3030 Bristol St, Costa Mesa, CA 92626", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Newport Beach", address: "4711 Birch St, Newport Beach, CA 92660", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Irvine", address: "18007 Von Karman Ave, Irvine, CA 92612", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Huntington Beach", address: "21132 Beach Blvd, Huntington Beach, CA 92648", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Laguna Beach", address: "2000 S Coast Hwy, Laguna Beach, CA 92651", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Costa Mesa", address: "405 E 17th St, Costa Mesa, CA 92627", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Irvine", address: "1150 Avenue of the Arts, Costa Mesa, CA 92626", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Costa Mesa",
      address: "2260 East, Newport Blvd, Costa Mesa, CA 92627",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Santa Ana",
      address: "1330 E First St, Santa Ana, CA 92701",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Laguna Hills",
      address: "23535 El Toro Rd, Lake Forest, CA 92630",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Fullerton",
      address: "2525 W Orangethorpe Ave, Fullerton, CA 92833",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for northern Orange County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SNA",
      name: "John Wayne Airport (Orange County)",
      distance: "5 miles from Newport Beach",
      address: "18601 Airport Way, Santa Ana, CA 92707",
      note: "The primary airport for Orange County. International flights to Mexico, Canada, and connections to Europe and Asia.",
      flightUrl: "https://www.ocair.com/",
      flightLabel: "John Wayne Airport",
      featured: true,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "45 miles from Newport Beach",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via I-405.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
    {
      code: "LGB",
      name: "Long Beach Airport",
      distance: "20 miles from Newport Beach",
      address: "4100 Donald Douglas Dr, Long Beach, CA 90808",
      note: "Convenient for domestic flights and some international connections. Less crowded than LAX.",
      flightUrl: "https://www.longbeach.gov/lgb/",
      flightLabel: "Long Beach Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [{ icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from California CDPH Vital Records for Orange County.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" }, { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through California CDPH Vital Records.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" }, { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Orange County Clerk-Recorder.", url: "https://www.occr.ocgov.com/" }, { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through Orange County Superior Court.", url: "https://www.occourts.org/" }],
};